import { useQuery, hashQueryKey, useQueryClient, useMutation } from 'react-query';
import { fetchSignInMethodsForEmail, getRedirectResult } from 'firebase/auth';
import { applyActionCode, checkActionCode, confirmPasswordReset, createUserWithEmailAndPassword, deleteUser, linkWithCredential, linkWithPhoneNumber, linkWithPopup, linkWithRedirect, reauthenticateWithCredential, reauthenticateWithPhoneNumber, reauthenticateWithPopup, reauthenticateWithRedirect, reload, sendEmailVerification, sendPasswordResetEmail, sendSignInLinkToEmail, signInAnonymously, signInWithCredential, signInWithCustomToken, signInWithEmailAndPassword, signInWithEmailLink, signInWithPhoneNumber, signInWithPopup, signInWithRedirect, signOut, unlink, updateCurrentUser, updateEmail, updatePassword, updatePhoneNumber, updateProfile, verifyBeforeUpdateEmail, verifyPasswordResetCode } from '@firebase/auth';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var firestoreUnsubscribes = {};
var queryCacheUnsubscribes = {};
var eventCount = {};
function firestoreUnsubscribe(subscriptionHash) {
    var firestoreUnsubscribe = firestoreUnsubscribes[subscriptionHash];
    if (firestoreUnsubscribe && typeof firestoreUnsubscribe === "function") {
        firestoreUnsubscribe();
    }
    delete firestoreUnsubscribes[subscriptionHash];
    delete eventCount[subscriptionHash];
}
function queryCacheUnsubscribe(subscriptionHash) {
    var queryCacheUnsubscribe = queryCacheUnsubscribes[subscriptionHash];
    if (queryCacheUnsubscribe) {
        queryCacheUnsubscribe();
        delete queryCacheUnsubscribes[subscriptionHash];
    }
}
/**
 * Utility hook to subscribe to events, given a function that returns an observer callback.
 * @param queryKey The react-query queryKey
 * @param subscriptionKey A hashable key to store the subscription
 * @param subscribeFn Returns an unsubscribe function to the event
 * @param options
 * @returns
 */
function useSubscription(queryKey, subscriptionKey, subscribeFn, options) {
    var _this = this;
    var _a;
    var hashFn = (options === null || options === void 0 ? void 0 : options.queryKeyHashFn) || hashQueryKey;
    var subscriptionHash = hashFn(subscriptionKey);
    var queryClient = useQueryClient();
    var resolvePromise = function () { return null; };
    var rejectPromise = function () { return null; };
    var result = new Promise(function (resolve, reject) {
        resolvePromise = resolve;
        rejectPromise = reject;
    });
    result.cancel = function () {
        queryClient.invalidateQueries(queryKey);
    };
    if (options === null || options === void 0 ? void 0 : options.onlyOnce) {
        if (!options.fetchFn) {
            throw new Error("You must specify fetchFn if using onlyOnce mode.");
        }
        else {
            var enabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
            if (enabled) {
                options
                    .fetchFn()
                    .then(resolvePromise)
                    .catch(function (err) {
                    rejectPromise(err);
                });
            }
        }
    }
    else {
        var subscribedToQueryCache = !!queryCacheUnsubscribes[subscriptionHash];
        if (!subscribedToQueryCache) {
            var queryCache = queryClient.getQueryCache();
            queryCacheUnsubscribes[subscriptionHash] = queryCache.subscribe(function (event) {
                if (!event || event.query.queryHash !== hashFn(queryKey)) {
                    return;
                }
                var query = event.query, type = event.type;
                if (type === "queryRemoved") {
                    delete eventCount[subscriptionHash];
                    queryCacheUnsubscribe(subscriptionHash);
                    firestoreUnsubscribe(subscriptionHash);
                }
                if (type === "observerAdded" || type === "observerRemoved") {
                    var observersCount = query.getObserversCount();
                    if (observersCount === 0) {
                        firestoreUnsubscribe(subscriptionHash);
                    }
                    else {
                        var isSubscribedToFirestore = !!firestoreUnsubscribes[subscriptionHash];
                        if (isSubscribedToFirestore) {
                            var cachedData = queryClient.getQueryData(queryKey);
                            var hasData = !!eventCount[subscriptionHash];
                            if (hasData) {
                                resolvePromise(cachedData !== null && cachedData !== void 0 ? cachedData : null);
                            }
                        }
                        else {
                            firestoreUnsubscribes[subscriptionHash] = subscribeFn(function (data) { return __awaiter(_this, void 0, void 0, function () {
                                var _a;
                                return __generator(this, function (_b) {
                                    (_a = eventCount[subscriptionHash]) !== null && _a !== void 0 ? _a : (eventCount[subscriptionHash] = 0);
                                    eventCount[subscriptionHash]++;
                                    if (eventCount[subscriptionHash] === 1) {
                                        resolvePromise(data || null);
                                    }
                                    else {
                                        queryClient.setQueryData(queryKey, data);
                                    }
                                    return [2 /*return*/];
                                });
                            }); });
                        }
                    }
                }
            });
        }
    }
    var queryFn = function () {
        return result;
    };
    return useQuery(__assign(__assign({}, options), { queryFn: queryFn, queryKey: queryKey, retry: false, staleTime: Infinity, refetchInterval: undefined, refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false }));
}

function useAuthUser(queryKey, auth, options) {
    if (options === void 0) { options = {}; }
    var subscribeFn = function (cb) {
        return auth.onAuthStateChanged(cb);
    };
    return useSubscription(queryKey, ["useAuthUser", queryKey], subscribeFn, options);
}

function useAuthIdToken(queryKey, auth, options) {
    var _this = this;
    if (options === void 0) { options = {}; }
    var subscribeFn = function (callback) {
        return auth.onIdTokenChanged(function (data) { return __awaiter(_this, void 0, void 0, function () {
            var token;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, (data === null || data === void 0 ? void 0 : data.getIdTokenResult())];
                    case 1:
                        token = _a.sent();
                        return [2 /*return*/, callback(token ? { token: token } : null)];
                }
            });
        }); });
    };
    return useSubscription(queryKey, ["useAuthIdToken", queryKey], subscribeFn, options);
}

function useAuthFetchSignInMethodsForEmail(key, auth, email, useQueryOptions) {
    var _a;
    return useQuery(__assign(__assign({}, useQueryOptions), { queryKey: (_a = useQueryOptions === null || useQueryOptions === void 0 ? void 0 : useQueryOptions.queryKey) !== null && _a !== void 0 ? _a : key, queryFn: function () {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    return [2 /*return*/, fetchSignInMethodsForEmail(auth, email)];
                });
            });
        } }));
}

function useAuthGetRedirectResult(key, auth, resolver, useQueryOptions) {
    var _a;
    return useQuery(__assign(__assign({}, useQueryOptions), { queryKey: (_a = useQueryOptions === null || useQueryOptions === void 0 ? void 0 : useQueryOptions.queryKey) !== null && _a !== void 0 ? _a : key, queryFn: function () {
            return __awaiter(this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    return [2 /*return*/, getRedirectResult(auth, resolver)];
                });
            });
        } }));
}

function useAuthApplyActionCode(auth, useMutationOptions) {
    return useMutation(function (oobCode) {
        return applyActionCode(auth, oobCode);
    }, useMutationOptions);
}
function useAuthCheckActionCode(auth, useMutationOptions) {
    return useMutation(function (oobCode) {
        return checkActionCode(auth, oobCode);
    }, useMutationOptions);
}
function useAuthConfirmPasswordReset(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var oobCode = _a.oobCode, newPassword = _a.newPassword;
        return confirmPasswordReset(auth, oobCode, newPassword);
    }, useMutationOptions);
}
function useAuthCreateUserWithEmailAndPassword(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var email = _a.email, password = _a.password;
        return createUserWithEmailAndPassword(auth, email, password);
    }, useMutationOptions);
}
function useAuthDeleteUser(useMutationOptions) {
    return useMutation(function (user) {
        return deleteUser(user);
    }, useMutationOptions);
}
function useAuthLinkWithCredential(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, credential = _a.credential;
        return linkWithCredential(user, credential);
    }, useMutationOptions);
}
function useAuthLinkWithPhoneNumber(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, phoneNumber = _a.phoneNumber, appVerifier = _a.appVerifier;
        return linkWithPhoneNumber(user, phoneNumber, appVerifier);
    }, useMutationOptions);
}
function useAuthLinkWithPopup(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, provider = _a.provider, resolver = _a.resolver;
        return linkWithPopup(user, provider, resolver);
    }, useMutationOptions);
}
function useAuthLinkWithRedirect(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, provider = _a.provider, resolver = _a.resolver;
        return linkWithRedirect(user, provider, resolver);
    }, useMutationOptions);
}
function useAuthReauthenticateWithCredential(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, credential = _a.credential;
        return reauthenticateWithCredential(user, credential);
    }, useMutationOptions);
}
function useAuthReauthenticateWithPhoneNumber(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, phoneNumber = _a.phoneNumber, appVerifier = _a.appVerifier;
        return reauthenticateWithPhoneNumber(user, phoneNumber, appVerifier);
    }, useMutationOptions);
}
function useAuthReauthenticateWithPopup(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, provider = _a.provider, resolver = _a.resolver;
        return reauthenticateWithPopup(user, provider, resolver);
    }, useMutationOptions);
}
function useAuthReauthenticateWithRedirect(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, provider = _a.provider, resolver = _a.resolver;
        return reauthenticateWithRedirect(user, provider, resolver);
    }, useMutationOptions);
}
function useAuthReload(useMutationOptions) {
    return useMutation(function (user) {
        return reload(user);
    }, useMutationOptions);
}
function useAuthSendEmailVerification(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, actionCodeSettings = _a.actionCodeSettings;
        return sendEmailVerification(user, actionCodeSettings);
    }, useMutationOptions);
}
function useAuthSendPasswordResetEmail(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var email = _a.email, actionCodeSettings = _a.actionCodeSettings;
        return sendPasswordResetEmail(auth, email, actionCodeSettings);
    }, useMutationOptions);
}
function useAuthSendSignInLinkToEmail(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var email = _a.email, actionCodeSettings = _a.actionCodeSettings;
        return sendSignInLinkToEmail(auth, email, actionCodeSettings);
    }, useMutationOptions);
}
function useAuthSignInAnonymously(auth, useMutationOptions) {
    return useMutation(function () {
        return signInAnonymously(auth);
    }, useMutationOptions);
}
function useAuthSignInWithCredential(auth, useMutationOptions) {
    return useMutation(function (credential) {
        return signInWithCredential(auth, credential);
    }, useMutationOptions);
}
function useAuthSignInWithCustomToken(auth, useMutationOptions) {
    return useMutation(function (customToken) {
        return signInWithCustomToken(auth, customToken);
    }, useMutationOptions);
}
function useAuthSignInWithEmailAndPassword(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var email = _a.email, password = _a.password;
        return signInWithEmailAndPassword(auth, email, password);
    }, useMutationOptions);
}
function useAuthSignInWithEmailLink(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var email = _a.email, emailLink = _a.emailLink;
        return signInWithEmailLink(auth, email, emailLink);
    }, useMutationOptions);
}
function useAuthSignInWithPhoneNumber(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var phoneNumber = _a.phoneNumber, appVerifier = _a.appVerifier;
        return signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    }, useMutationOptions);
}
function useAuthSignInWithPopup(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var provider = _a.provider, resolver = _a.resolver;
        return signInWithPopup(auth, provider, resolver);
    }, useMutationOptions);
}
function useAuthSignInWithRedirect(auth, useMutationOptions) {
    return useMutation(function (_a) {
        var provider = _a.provider, resolver = _a.resolver;
        return signInWithRedirect(auth, provider, resolver);
    }, useMutationOptions);
}
function useAuthSignOut(auth, useMutationOptions) {
    return useMutation(function () {
        return signOut(auth);
    }, useMutationOptions);
}
function useAuthUnlink(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, providerId = _a.providerId;
        return unlink(user, providerId);
    }, useMutationOptions);
}
function useAuthUpdateCurrentUser(auth, useMutationOptions) {
    return useMutation(function (user) {
        return updateCurrentUser(auth, user);
    }, useMutationOptions);
}
function useAuthUpdateEmail(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, newEmail = _a.newEmail;
        return updateEmail(user, newEmail);
    }, useMutationOptions);
}
function useAuthUpdatePassword(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, newPassword = _a.newPassword;
        return updatePassword(user, newPassword);
    }, useMutationOptions);
}
function useAuthUpdatePhoneNumber(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, credential = _a.credential;
        return updatePhoneNumber(user, credential);
    }, useMutationOptions);
}
function useAuthUpdateProfile(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, update = __rest(_a, ["user"]);
        return updateProfile(user, update);
    }, useMutationOptions);
}
function useAuthVerifyBeforeUpdateEmail(useMutationOptions) {
    return useMutation(function (_a) {
        var user = _a.user, newEmail = _a.newEmail, actionCodeSettings = _a.actionCodeSettings;
        return verifyBeforeUpdateEmail(user, newEmail, actionCodeSettings);
    }, useMutationOptions);
}
function useAuthVerifyPasswordResetCode(auth, useMutationOptions) {
    return useMutation(function (code) {
        return verifyPasswordResetCode(auth, code);
    }, useMutationOptions);
}

export { useAuthApplyActionCode, useAuthCheckActionCode, useAuthConfirmPasswordReset, useAuthCreateUserWithEmailAndPassword, useAuthDeleteUser, useAuthFetchSignInMethodsForEmail, useAuthGetRedirectResult, useAuthIdToken, useAuthLinkWithCredential, useAuthLinkWithPhoneNumber, useAuthLinkWithPopup, useAuthLinkWithRedirect, useAuthReauthenticateWithCredential, useAuthReauthenticateWithPhoneNumber, useAuthReauthenticateWithPopup, useAuthReauthenticateWithRedirect, useAuthReload, useAuthSendEmailVerification, useAuthSendPasswordResetEmail, useAuthSendSignInLinkToEmail, useAuthSignInAnonymously, useAuthSignInWithCredential, useAuthSignInWithCustomToken, useAuthSignInWithEmailAndPassword, useAuthSignInWithEmailLink, useAuthSignInWithPhoneNumber, useAuthSignInWithPopup, useAuthSignInWithRedirect, useAuthSignOut, useAuthUnlink, useAuthUpdateCurrentUser, useAuthUpdateEmail, useAuthUpdatePassword, useAuthUpdatePhoneNumber, useAuthUpdateProfile, useAuthUser, useAuthVerifyBeforeUpdateEmail, useAuthVerifyPasswordResetCode };
